import { inject, Injectable, OnDestroy } from '@angular/core';
import { ApiService } from './api.service';
import { catchError, finalize, map, Observable, of, share, Subject, Subscription, tap } from 'rxjs';
import { StoredContext, StoredData, StoredState, StoredStateFilter } from '../model/api';
import { fromPairs, isPlainObject, sortBy, toPairs } from 'lodash-es';
import { BoardId, MarketId, Sort } from '../model/common';
import { SymbolTicker } from '../../terminal/symbols/model';
import { AuthenticatorService } from './authenticator.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FeedFilterTag } from '../../terminal/feed/feed-filter.service';
import { OrdersSortKey } from 'src/app/terminal/orders/model';
import { PositionsSortKey } from 'src/app/terminal/positions/model';

@Injectable({
  providedIn: 'root'
})
export class StoredStateService implements OnDestroy {

  private authenticator = inject(AuthenticatorService)
  private api = inject(ApiService)
  private cache = new Map<string, StoredState>()

  private sb = this.authenticator.auth$.subscribe(() => this.cache.clear())

  ngOnDestroy(): void {
    this.sb.unsubscribe()
  }

  getState<C extends StoredContext, D extends StoredData>(filters: StoredStateFilter<C>[]): Observable<StoredState<C, D>[] | undefined> {
    const fromCache: StoredState<C, D>[] = []              // состояния, полученные из кеша
    const filtersForRequest: StoredStateFilter<C>[] = []   // фильтры, которые надо прокинуть в запрос

    filters.forEach(filter => {
      const result = this.getStateFromCache<C, D>(filter)
      if (result) {
        fromCache.push(result)
      } else {
        filtersForRequest.push(filter)
      }
    })

    if (this.authenticator.isAuthed && filtersForRequest.length > 0) {
      return this.api.post<StoredState<C, D>[]>(`/state/get-state?${filters[0].name}`, {filters: filtersForRequest}).pipe(
        tap(data => data.forEach(state => this.setStateToCache(state))),
        map(data => [...fromCache, ...data] as StoredState<C, D>[]),
        catchError(err => {
          if (err instanceof HttpErrorResponse && err.status == 401) {
            return of(undefined)
          }
          throw err
        })
      )
    } else if (fromCache.length) {
      return of(fromCache)
    }
    return of(undefined)
  }

  setState<C extends StoredContext, D extends StoredData>(states: StoredState<C, D>[]): Observable<unknown> {
    states.forEach(state => this.setStateToCache(state))
    return this.authenticator.isAuthed ? this.api.post(`/state/set-state?${states[0].name}`, {states}) : of(undefined)
  }

  deleteState<C extends StoredContext>(filters: StoredStateFilter<C>[]): Observable<unknown> {
    filters.forEach(filter => this.deleteStateFromCache(filter))
    return this.authenticator.isAuthed ? this.api.post(`/state/delete-state?${filters[0].name}`, {filters}) : of(undefined)
  }

  private getStateFromCache<C extends StoredContext, D extends StoredData>(filter: StoredStateFilter<C>) {
    const key = generateStateKey(filter)
    return this.cache.get(key) as StoredState<C, D> | undefined
  }

  private setStateToCache<C extends StoredContext, D extends StoredData>(state: StoredState<C, D>) {
    const key = generateStateKey(state)
    this.cache.set(key, state)
  }

  private deleteStateFromCache<C extends StoredContext>(filter: StoredStateFilter<C>) {
    const key = generateStateKey(filter)
    this.cache.delete(key)
  }

}


@Injectable({
  providedIn: 'root'
})
export class StoredStateHelper {
  private service = inject(StoredStateService)
  private requests = new Map<string, Observable<any>>()
  private subscriptions = new Map<string, Subscription>()

  private stdGet<C extends StoredContext, D extends StoredData>(filter: StoredStateFilter<C>) {
    const key = 'get#' + generateStateKey(filter)
    const req: Observable<D | undefined> = this.requests.get(key) ?? this.service.getState<C, D>([filter]).pipe(
      finalize(() => this.requests.delete(key)),
      map(data => data && data[0] ? data[0].data : undefined),
      share()
    )
    this.requests.set(key, req)
    return req
  }

  private stdSet<C extends StoredContext, D extends StoredData>(state: StoredState<C, D>) {
    const subject = new Subject<unknown>()
    const key = 'set#' + generateStateKey(state)
    let sb = this.subscriptions.get(key)
    sb?.unsubscribe()
    sb = this.service.setState<C, D>([state]).subscribe(subject)
    this.subscriptions.set(key, sb)
    return subject.asObservable()
  }

  private stdDelete<C extends StoredContext>(filter: StoredStateFilter<C>) {
    const subject = new Subject<unknown>()
    const key = 'delete#' + generateStateKey(filter)
    let sb = this.subscriptions.get(key)
    sb?.unsubscribe()
    sb = this.service.deleteState<C>([filter]).subscribe(subject)
    this.subscriptions.set(key, sb)
    return subject.asObservable()
  }

  setTerminalActiveKey(keyId: number) {
    return this.stdSet<string, number>({name: 'terminal.activeKey', context: 'global', data: keyId})
  }

  getTerminalActiveKey() {
    return this.stdGet<string, number>({name: 'terminal.activeKey', context: 'global'})
  }

  setTerminalSymbolsSort(sort: Sort<keyof SymbolTicker>) {
    return this.stdSet<string, Sort<keyof SymbolTicker>>({name: 'terminal.symbolsSort', context: 'global', data: sort})
  }

  getTerminalSymbolsSort() {
    return this.stdGet<string, Sort<keyof SymbolTicker>>({name: 'terminal.symbolsSort', context: 'global'})
  }

  setTerminalSymbolsSearch(market: MarketId, pattern: string) {
    return this.stdSet<Record<string, MarketId>, string>({name: 'terminal.symbolsSearch', context: {market}, data: pattern})
  }

  getTerminalSymbolsSearch(market: MarketId) {
    return this.stdGet<Record<string, MarketId>, string>({name: 'terminal.symbolsSearch', context: {market}})
  }

  setTerminalActiveSymbol(market: MarketId, board: BoardId, symbol: string) {
    return this.stdSet<{market: MarketId, board: BoardId}, string>({name: 'terminal.activeSymbol', context: {market, board}, data: symbol})
  }

  getTerminalActiveSymbol(market: MarketId, board: BoardId) {
    return this.stdGet<{market: MarketId, board: BoardId}, string>({name: 'terminal.activeSymbol', context: {market, board}})
  }

  setTerminalActiveCoin(keyId: number, coin: string) {
    return this.stdSet<Record<string, number>, string>({name: 'terminal.activeCoin', context: {keyId}, data: coin})
  }

  getTerminalActiveCoin(keyId: number) {
    return this.stdGet<Record<string, number>, string>({name: 'terminal.activeCoin', context: {keyId}})
  }

  setTerminalWatchlist(market: MarketId, watchlist: string[]) {
    return this.stdSet<Record<string, MarketId>, string[]>({name: 'terminal.watchlist', context: {market}, data: watchlist})
  }

  getTerminalWatchlist(market: MarketId) {
    return this.stdGet<Record<string, MarketId>, string[]>({name: 'terminal.watchlist', context: {market}})
  }

  setTerminalFeedFilterTag(tag: FeedFilterTag) {
    return this.stdSet<string, FeedFilterTag>({name: 'terminal.feed.filter.tag', context: 'global', data: tag})
  }

  getTerminalFeedFilterTag() {
    return this.stdGet<string, FeedFilterTag>({name: 'terminal.feed.filter.tag', context: 'global'})
  }

  setTerminalOrdersSort(sort: Sort<OrdersSortKey>) {
    return this.stdSet<string, Sort<OrdersSortKey>>({name: 'terminal.ordersSort', context: 'global', data: sort})
  }

  getTerminalOrdersSort() {
    return this.stdGet<string, Sort<OrdersSortKey>>({name: 'terminal.ordersSort', context: 'global'})
  }

  setTerminalPositionsSort(sort: Sort<PositionsSortKey>) {
    return this.stdSet<string, Sort<PositionsSortKey>>({name: 'terminal.positionsSort', context: 'global', data: sort})
  }

  getTerminalPositionsSort() {
    return this.stdGet<string, Sort<PositionsSortKey>>({name: 'terminal.positionsSort', context: 'global'})
  }

  setTerminalWorkVolume(keyId: number, workVolume: number) {
    return this.stdSet<Record<string, number>, number>({name: 'terminal.workVolume', context: {keyId}, data: workVolume})
  }

  getTerminalWorkVolume(keyId: number) {
    return this.stdGet<Record<string, number>, number>({name: 'terminal.workVolume', context: {keyId}})
  }

  setTerminalDealsVisibility(flag: boolean) {
    return this.stdSet<string, boolean>({name: 'terminal.dealsVisibility', context: 'global', data: flag})
  }

  getTerminalDealsVisibility() {
    return this.stdGet<string, boolean>({name: 'terminal.dealsVisibility', context: 'global'})
  }

  setAiModelSelected(model: string) {
    return this.stdSet<string, string>({name: 'ai.modelSelected', context: 'global', data: model})
  }

  getAiModelSelected() {
    return this.stdGet<string, string>({name: 'ai.modelSelected', context: 'global'})
  }

  setAiMarketSelected(market: MarketId) {
    return this.stdSet<string, MarketId>({name: 'ai.marketSelected', context: 'global', data: market})
  }

  getAiMarketSelected() {
    return this.stdGet<string, MarketId>({name: 'ai.marketSelected', context: 'global'})
  }

  getAppTheme<T extends string>() {
    return this.stdGet<string, T>({name: 'app.theme', context: 'global'})
  }

  setAppTheme<T extends string>(theme: T) {
    return this.stdSet<string, T>({name: 'app.theme', context: 'global', data: theme})
  }

  getTerminalDealsSound() {
    return this.stdGet<string, boolean>({name: 'terminal.dealsSound', context: 'global'})
  }

  setTerminalDealsSound(flag: boolean) {
    return this.stdSet<string, boolean>({name: 'terminal.dealsSound', context: 'global', data: flag})
  }

  setAppLang(lang: string) {
    return this.stdSet<string, string>({name: 'app.lang', context: 'global', data: lang})
  }

  getAppLang() {
    return this.stdGet<string, string>({name: 'app.lang', context: 'global'})
  }
}



function generateStateKey<C extends StoredContext>(state: StoredStateFilter<C>) {
  const key = state.name + '#'
  if (typeof(state.context) == 'object') {
    return key + JSON.stringify(deepSortObject(state.context))
  }
  return key + JSON.stringify(state.context)
}

function deepSortObject(obj: Record<string, any>): Record<string, any> {
  if (!isPlainObject(obj)) return obj
  return fromPairs(sortBy(toPairs(obj), 0).map(([key, value]) => [key, deepSortObject(value)]))
}
